import { useCallback, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { adminUpload } from '@/lib/api'
import type { UploadResult } from '@/types'
import { PageMotion } from '@/components/Motion'
import { PageHeader, Panel } from '@/components/ui'
import { cn } from '@/lib/utils'
import { CheckCircle2, FileJson, Loader2, Upload, XCircle } from 'lucide-react'

type FileJob = {
  file: File
  status: 'queued' | 'uploading' | 'done' | 'error'
  result?: UploadResult
}

export default function AdminUpload() {
  const [jobs, setJobs] = useState<FileJob[]>([])
  const [dragging, setDragging] = useState(false)
  const [running, setRunning] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const addFiles = useCallback((list: FileList | File[]) => {
    const files = [...list].filter((f) => f.name.toLowerCase().endsWith('.json'))
    if (!files.length) return
    setJobs((prev) => {
      const existing = new Set(prev.map((j) => j.file.name + j.file.size))
      const next = files
        .filter((f) => !existing.has(f.name + f.size))
        .map((file) => ({ file, status: 'queued' as const }))
      return [...prev, ...next]
    })
  }, [])

  const uploadAll = async () => {
    const pending = jobs.filter((j) => j.status === 'queued' || j.status === 'error')
    if (!pending.length || running) return
    setRunning(true)

    // Mark uploading
    setJobs((prev) =>
      prev.map((j) =>
        pending.some((p) => p.file === j.file) ? { ...j, status: 'uploading' as const } : j,
      ),
    )

    try {
      const results = await adminUpload(pending.map((p) => p.file))
      setJobs((prev) =>
        prev.map((j) => {
          const idx = pending.findIndex((p) => p.file === j.file)
          if (idx < 0) return j
          const result = results[idx]
          if (!result) {
            return { ...j, status: 'error' as const, result: { file: j.file.name, ok: false, error: 'No result' } }
          }
          return {
            ...j,
            status: result.ok ? ('done' as const) : ('error' as const),
            result,
          }
        }),
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed'
      setJobs((prev) =>
        prev.map((j) =>
          pending.some((p) => p.file === j.file)
            ? {
                ...j,
                status: 'error' as const,
                result: { file: j.file.name, ok: false, error: message },
              }
            : j,
        ),
      )
    } finally {
      setRunning(false)
    }
  }

  const clearDone = () => setJobs((prev) => prev.filter((j) => j.status !== 'done'))
  const clearAll = () => setJobs([])

  const done = jobs.filter((j) => j.status === 'done').length
  const failed = jobs.filter((j) => j.status === 'error').length

  return (
    <PageMotion className="space-y-8">
      <PageHeader
        title="Upload"
        subtitle="Drop multiple mission JSON exports — they import in one batch"
      />

      <div
        className={cn(
          'rounded-panel border border-dashed px-6 py-12 text-center transition-colors cursor-pointer',
          dragging
            ? 'border-accent bg-accent-soft'
            : 'border-line bg-canvas-raised/40 hover:border-line-soft',
        )}
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          addFiles(e.dataTransfer.files)
        }}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
        }}
      >
        <Upload className="mx-auto mb-3 text-ink-faint" size={28} />
        <p className="text-sm text-ink">Drop .json files here or click to browse</p>
        <p className="mt-1 text-xs text-ink-faint">Multiple files supported</p>
        <input
          ref={inputRef}
          type="file"
          accept=".json,application/json"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </div>

      {jobs.length > 0 && (
        <Panel
          title={`${jobs.length} file${jobs.length === 1 ? '' : 's'}`}
          action={
            <div className="flex gap-2">
              <button type="button" className="btn-ghost !py-1.5 text-xs" onClick={clearDone}>
                Clear done
              </button>
              <button type="button" className="btn-ghost !py-1.5 text-xs" onClick={clearAll}>
                Clear all
              </button>
              <button
                type="button"
                className="btn-primary !py-1.5 text-xs"
                disabled={running || !jobs.some((j) => j.status === 'queued' || j.status === 'error')}
                onClick={uploadAll}
              >
                {running ? 'Uploading…' : failed ? 'Retry failed' : 'Upload all'}
              </button>
            </div>
          }
        >
          {(done > 0 || failed > 0) && (
            <p className="text-xs text-ink-faint mb-3">
              {done} imported · {failed} failed
            </p>
          )}
          <ul className="space-y-2">
            {jobs.map((j) => (
              <motion.li
                key={j.file.name + j.file.size}
                layout
                className="flex items-start gap-3 rounded-panel border border-line bg-canvas-overlay/40 px-3 py-2.5 text-sm"
              >
                <FileJson size={16} className="mt-0.5 text-ink-faint shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-ink">{j.file.name}</div>
                  <div className="text-xs text-ink-faint mt-0.5">
                    {(j.file.size / 1024).toFixed(1)} KB
                    {j.result?.ok && j.result.missionId != null ? (
                      <>
                        {' · '}
                        <Link
                          to={`/missions/${j.result.missionId}`}
                          className="text-accent-text hover:underline"
                        >
                          Mission #{j.result.missionId}
                        </Link>
                        {j.result.message ? ` · ${j.result.message}` : ''}
                      </>
                    ) : null}
                    {j.result && !j.result.ok ? (
                      <span className="text-rose-400"> · {j.result.error}</span>
                    ) : null}
                  </div>
                </div>
                <StatusIcon status={j.status} />
              </motion.li>
            ))}
          </ul>
        </Panel>
      )}
    </PageMotion>
  )
}

function StatusIcon({ status }: { status: FileJob['status'] }) {
  if (status === 'uploading') return <Loader2 size={16} className="animate-spin text-accent" />
  if (status === 'done') return <CheckCircle2 size={16} className="text-accent-text" />
  if (status === 'error') return <XCircle size={16} className="text-rose-400" />
  return <span className="text-[10px] uppercase tracking-wider text-ink-faint">Queued</span>
}
